import { TooltipTrigger } from '../../../../../Provider/TooltipManager.js'
import type { SingleValue } from 'react-select'
import Select from 'react-select'
import type { CommonConfig, CSVColumnSelectorValidationState } from './CSVCommonState.js'
import { useDialogPortal } from '../../../../../Provider/DialogPortalContext.js'

export type SelectOption = {
  value: string
  label: string
  rawKey: string
}

export type CSVMetadataSharedSelectProps<TConfig extends CommonConfig> = {
  config: TConfig
  disabled?: boolean
  selectedValue: SelectOption | null
  options: SelectOption[]
  onChange: (s: SingleValue<SelectOption>) => void
  validationState: CSVColumnSelectorValidationState
}

/**
 * The rendering component for the shared part of the "select a column to get values from your csv" logic
 * @param props
 * @constructor
 */
export function CSVMetadataSharedSelect<TConfig extends CommonConfig>(props: CSVMetadataSharedSelectProps<TConfig>) {
  const { dialogRef } = useDialogPortal()

  return (
    <>
      <TooltipTrigger tooltip={props.config.helpText} disabled={!props.config.helpText}>
        <label className="whitespace-nowrap">{props.config.label}</label>
      </TooltipTrigger>
      <TooltipTrigger tooltip={props.config.helpText} disabled={!props.config.helpText}>
        <div className="flex-1 min-w-0 max-w-[18rem]">
          <Select
            isDisabled={props.disabled}
            inputId={`csv-${props.config.label}-id`}
            value={props.selectedValue}
            onChange={props.onChange}
            options={props.options}
            menuPosition={'fixed'}
            menuPortalTarget={dialogRef?.current}
            className="react-select"
            classNamePrefix="select"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              singleValue: (base) => ({
                ...base,
                fontStyle: props.validationState === 'NO_DATA' ? 'italic' : 'inherit',
                fontWeight: props.validationState === 'NO_DATA' ? 'inherit' : 'bold'
              }),
              control: (base, state) => ({
                ...base,
                borderColor:
                  props.validationState === 'VALIDATED' ? '#d1d5db' : props.validationState === 'INVALID_VALUES' ? '#ef4444' : '#d1d5db',
                borderWidth: '1px',
                borderRadius: '0.375rem',
                borderLeft:
                  props.validationState === 'VALIDATED'
                    ? '4px solid #10b981'
                    : props.validationState === 'INVALID_VALUES'
                      ? '4px solid #ef4444'
                      : base.borderLeft,
                fontSize: '0.875rem',
                minHeight: '2rem',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: state.isFocused ? '#3b82f6' : base.borderColor
                }
              }),
              option: (base) => ({
                ...base,
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                fontSize: '0.875rem'
              }),
              menu: (base) => ({
                ...base,
                zIndex: 1000
              })
            }}
            placeholder="Select Column matching the criteria"
          />
        </div>
      </TooltipTrigger>
    </>
  )
}
